import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BugReportClientContext } from '../models';

/**
 * Captures lightweight client-side diagnostics that feed the bug report
 * dialog's `clientContext` payload. Singleton: window listeners are wired
 * once at app startup so we don't miss errors that fire before the first
 * HTTP call.
 *
 * What we record:
 *   - last HTTP error (URL/method/status/body, body sliced to 2 KB)
 *   - last uncaught JS error or unhandled promise rejection
 *   - a rolling buffer of breadcrumbs: route navigations, HTTP errors,
 *     JS errors. Successful requests are intentionally NOT recorded —
 *     they're noisy and rarely useful for triage.
 *
 * Privacy/cost notes:
 *   - bodies are stringified and truncated to 2 KB (backend caps the full
 *     clientContext payload at 64 KB encoded)
 *   - we never read Authorization headers
 *   - the bug report POST itself is filtered upstream by the interceptor
 *     so it doesn't pollute its own trail.
 */
@Injectable({ providedIn: 'root' })
export class ClientDiagnosticsService {
  private static readonly MAX_BREADCRUMBS = 20;
  private static readonly MAX_BODY_LENGTH = 2000;
  private static readonly MAX_STACK_LENGTH = 4000;
  private static readonly MAX_CONSOLE_MSG = 500;

  private readonly router = inject(Router);

  private lastRequest: NonNullable<BugReportClientContext['failingRequest']> | null = null;
  private lastJsError: NonNullable<BugReportClientContext['jsError']> | null = null;
  private readonly breadcrumbs: NonNullable<BugReportClientContext['breadcrumbs']> = [];

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        this.pushBreadcrumb('navigation', { url: e.urlAfterRedirects });
      });

    if (typeof window !== 'undefined') {
      window.addEventListener('error', event => {
        this.lastJsError = {
          message: event.message || 'Unknown error',
          source: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: this.truncate(event.error?.stack, ClientDiagnosticsService.MAX_STACK_LENGTH),
        };
        this.pushBreadcrumb('jsError', { message: this.lastJsError.message });
      });

      window.addEventListener('unhandledrejection', event => {
        const reason = event.reason as { message?: string; stack?: string } | string | undefined;
        const message = typeof reason === 'string'
          ? reason
          : (reason?.message ?? 'Unhandled promise rejection');
        this.lastJsError = {
          message,
          stack: this.truncate(
            typeof reason === 'object' ? reason?.stack : undefined,
            ClientDiagnosticsService.MAX_STACK_LENGTH,
          ),
        };
        this.pushBreadcrumb('unhandledRejection', { message });
      });
    }

    this.patchConsole();
  }

  /** Called by the HTTP interceptor for any 4xx/5xx response. */
  recordHttpError(method: string, url: string, status: number, body: unknown): void {
    this.lastRequest = {
      url,
      method,
      status,
      body: this.summarizeBody(body),
    };
    this.pushBreadcrumb('httpError', { method, url, status });
  }

  /**
   * Snapshot the current diagnostic state for inclusion in a bug report.
   * Returns a fresh object — safe to mutate at the call site.
   */
  snapshot(): BugReportClientContext {
    const ctx: BugReportClientContext = {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      viewport: typeof window !== 'undefined'
        ? { width: window.innerWidth, height: window.innerHeight }
        : undefined,
      route: this.router.url,
      breadcrumbs: [...this.breadcrumbs],
    };
    if (this.lastRequest) ctx.failingRequest = { ...this.lastRequest };
    if (this.lastJsError) ctx.jsError = { ...this.lastJsError };
    return ctx;
  }

  private pushBreadcrumb(type: string, data?: unknown): void {
    this.breadcrumbs.push({ ts: new Date().toISOString(), type, data });
    if (this.breadcrumbs.length > ClientDiagnosticsService.MAX_BREADCRUMBS) {
      this.breadcrumbs.shift();
    }
  }

  private summarizeBody(body: unknown): string {
    if (body === null || body === undefined) return '';
    try {
      const s = typeof body === 'string' ? body : JSON.stringify(body);
      return this.truncate(s, ClientDiagnosticsService.MAX_BODY_LENGTH) ?? '';
    } catch {
      return '[unserializable]';
    }
  }

  private truncate(value: string | undefined | null, max: number): string | undefined {
    if (value == null) return undefined;
    return value.length > max ? value.slice(0, max) + '…' : value;
  }

  /**
   * Monkey-patch `console.error` and `console.warn` so explicit log calls
   * leave a breadcrumb. We only intercept error/warn levels — `log`/`info`/
   * `debug` are too chatty (Angular dev warnings, third-party libs) and
   * would evict the genuinely diagnostic events from the 20-slot buffer.
   *
   * Originals are still called, so the DevTools console behaves identically.
   * Side effect: the "logged from" line in DevTools points at this wrapper
   * instead of the original call site — acceptable trade-off for the
   * diagnostic value.
   */
  private patchConsole(): void {
    if (typeof console === 'undefined') return;
    const originalError = console.error.bind(console);
    const originalWarn = console.warn.bind(console);

    console.error = (...args: unknown[]) => {
      this.pushBreadcrumb('consoleError', { message: this.formatConsoleArgs(args) });
      originalError(...args);
    };
    console.warn = (...args: unknown[]) => {
      this.pushBreadcrumb('consoleWarn', { message: this.formatConsoleArgs(args) });
      originalWarn(...args);
    };
  }

  private formatConsoleArgs(args: unknown[]): string {
    const parts = args.map(arg => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) {
        return arg.stack ? `${arg.message}\n${arg.stack}` : arg.message;
      }
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    });
    return this.truncate(parts.join(' '), ClientDiagnosticsService.MAX_CONSOLE_MSG) ?? '';
  }
}
